import { pressKey } from "../../lib/mobile";
import type { useOutlineEngine } from "../../outline/controller";
import { useMobileChrome } from "./chrome";

/**
 * Панель действий — то, чем на телефоне заменена вся клавиатура.
 *
 * Стоит ровно там же, где на десктопе строка режима, и делает ровно то же: показывает,
 * что сейчас можно. Разница в том, что на десктопе это подсказка, а здесь — сами кнопки.
 * Набор меняется вместе с режимом, поэтому «в каком я режиме» не нужно ни помнить, ни
 * читать: видно по тому, что предлагают нажать.
 *
 * Панель приподнимается над клавиатурой (`--kb-inset`) — иначе отступ и новая строка,
 * то есть вся структурная правка, недоступны ровно в тот момент, когда человек печатает.
 */
export function ActionRail({ engine }: { engine?: ReturnType<typeof useOutlineEngine> | null }) {
	const { openMenu } = useMobileChrome();
	const editing = engine?.state.mode === "edit";

	return (
		<div className="action-rail" data-mode={editing ? "edit" : "nav"}>
			{editing && engine ? <EditActions /> : <NavActions engine={engine} onMenu={openMenu} />}
		</div>
	);
}

function NavActions({ engine, onMenu }: { engine?: ReturnType<typeof useOutlineEngine> | null; onMenu: () => void }) {
	const actions = engine?.actions;
	return (
		<>
			<RailButton label="Меню" onClick={onMenu}>
				<path d="M4 7h16M4 12h16M4 17h16" />
			</RailButton>

			{actions && (
				<>
					{/* Единственная кнопка, ради которой панель вообще нужна: без неё на телефоне
					    нечем завести строку — клавиатуру не вызвать, пока строки нет. */}
					<RailButton label="Новая строка" primary onClick={() => actions.createSibling("below")}>
						<path d="M12 5v14M5 12h14" />
					</RailButton>
					<RailButton label="Поиск" onClick={() => actions.setOverlay({ kind: "search" })}>
						<circle cx="11" cy="11" r="7" />
						<path d="M20 20l-3.6-3.6" />
					</RailButton>
					<RailButton label="Ещё" onClick={() => actions.setOverlay({ kind: "palette" })}>
						<path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth={3} />
					</RailButton>
				</>
			)}
		</>
	);
}

/**
 * В правке кнопки не вызывают действия напрямую, а нажимают те же клавиши: порядок
 * «дослать правку → запомнить каретку → переехать» живёт в редакторе строки одним
 * куском, и второй его копии быть не должно (см. `pressKey`).
 */
function EditActions() {
	return (
		<>
			<RailButton label="Влево" onClick={() => pressKey("Tab", { shift: true })}>
				<path d="M10 6h10M10 12h10M10 18h10M7 9l-3 3 3 3" />
			</RailButton>
			<RailButton label="Вправо" onClick={() => pressKey("Tab")}>
				<path d="M10 6h10M10 12h10M10 18h10M4 9l3 3-3 3" />
			</RailButton>
			<RailButton label="Строка" onClick={() => pressKey("Enter")}>
				<path d="M9 10l-4 4 4 4M5 14h9a5 5 0 0 0 5-5V6" />
			</RailButton>
			<RailButton
				label="Готово"
				primary
				onClick={() => {
					// Блюр — штатный выход из редактора: он же дошлёт правку.
					(document.activeElement as HTMLElement | null)?.blur();
				}}
			>
				<path d="M20 6L9 17l-5-5" />
			</RailButton>
		</>
	);
}

function RailButton({
	label,
	primary,
	onClick,
	children
}: {
	label: string;
	primary?: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			className="rail-btn"
			data-primary={primary ? "true" : undefined}
			// Фокус остаётся в редакторе: иначе нажатие кнопки блюрит `input`, правка
			// схлопывается, и «отступ» приезжает уже в режиме навигации.
			onMouseDown={(e) => e.preventDefault()}
			onClick={onClick}
		>
			<svg
				viewBox="0 0 24 24"
				width={20}
				height={20}
				fill="none"
				stroke="currentColor"
				strokeWidth={2}
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden="true"
			>
				{children}
			</svg>
			<span className="rail-label">{label}</span>
		</button>
	);
}
